import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Packages as PackagesApi, Pages } from "@/lib/api";
import EditableHTML from "@/components/EditableHTML";
import Footer from "@/components/Footer";
import QuoteForm from "@/components/QuoteForm";
import SporeBullet from "@/components/SporeBullet";
import { ArrowLeft } from "lucide-react";
import SEO, { SITE_URL } from "@/components/SEO";

const PackageDetail = () => {
  const { slug } = useParams();
  const [pkg, setPkg] = useState(null);
  const [home, setHome] = useState({});
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await PackagesApi.get(slug);
        setPkg(data);
      } catch (err) {
        // Only mark truly not-found on explicit HTTP 404 — never on network
        // errors, 5xx, or CORS blips (Google would cache the noindex verdict).
        if (err?.response?.status === 404) {
          setNotFound(true);
        }
      }
      try {
        const h = await Pages.get("home");
        setHome(h.sections || {});
      } catch {}
    })();
  }, [slug]);

  if (notFound) {
    return (
      <main className="min-h-screen pt-40 px-6 text-center text-slate-200" data-testid="package-not-found">
        <SEO title="Package not found" noindex path={`/equipment/${slug}`} />
        <div className="text-3xl font-display font-bold">Package not found</div>
        <Link to="/equipment" className="text-atc-blue mt-4 inline-block">← Back to equipment</Link>
      </main>
    );
  }

  if (!pkg) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;

  const productDescription =
    pkg.short_description ||
    `${pkg.name} — professional Thermification™ restoration equipment package from ATC Mold & Water. Contact us for full specifications and a tailored quote.`;

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: pkg.name,
    description: productDescription,
    image: pkg.image_url ? [pkg.image_url] : undefined,
    brand: { "@type": "Brand", name: "ATC Thermal · Thermification\u2122" },
    category: "Restoration Equipment",
    sku: pkg.slug,
    url: `${SITE_URL}/equipment/${pkg.slug}`,
    offers: {
      "@type": "Offer",
      url: `${SITE_URL}/equipment/${pkg.slug}`,
      priceCurrency: "USD",
      price: "0",
      availability: "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
      seller: { "@id": `${SITE_URL}/#organization` },
      priceSpecification: {
        "@type": "PriceSpecification",
        priceCurrency: "USD",
        valueAddedTaxIncluded: false,
        description: pkg.price || "Request a Quote",
      },
      // Custom-quoted equipment: buyer arranges freight; no standard returns.
      // Values reflect the real B2B fulfillment model to satisfy Google's
      // Merchant listing structured-data requirements without misleading users.
      shippingDetails: {
        "@type": "OfferShippingDetails",
        shippingRate: {
          "@type": "MonetaryAmount",
          value: "0",
          currency: "USD",
        },
        shippingDestination: {
          "@type": "DefinedRegion",
          addressCountry: "US",
        },
        deliveryTime: {
          "@type": "ShippingDeliveryTime",
          handlingTime: {
            "@type": "QuantitativeValue",
            minValue: 1,
            maxValue: 5,
            unitCode: "DAY",
          },
          transitTime: {
            "@type": "QuantitativeValue",
            minValue: 3,
            maxValue: 10,
            unitCode: "DAY",
          },
        },
      },
      hasMerchantReturnPolicy: {
        "@type": "MerchantReturnPolicy",
        applicableCountry: "US",
        returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
      },
    },
  };

  return (
    <main className="min-h-screen text-slate-100 pt-32">
      <SEO
        title={pkg.name}
        description={pkg.short_description || `Details on the ${pkg.name} Thermification™ equipment package from ATC Mold & Water.`}
        path={`/equipment/${pkg.slug}`}
        image={pkg.image_url || undefined}
        breadcrumbs={[
          { name: "Equipment", path: "/equipment" },
          { name: pkg.name, path: `/equipment/${pkg.slug}` },
        ]}
        schema={productSchema}
      />
      <section className="water-section max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="package-detail-page">
        <Link
          to="/equipment"
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition mb-10"
          data-testid="back-to-packages"
        >
          <ArrowLeft className="h-4 w-4" /> All Equipment
        </Link>

        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div
              className="aspect-[16/10] rounded-3xl bg-contain bg-center bg-no-repeat bg-slate-950/60 border border-white/10"
              style={{ backgroundImage: `url(${pkg.image_url})` }}
            />
            <h1 className="mt-10 font-display font-black text-4xl sm:text-5xl tracking-tight leading-tight chrome-gradient-text" data-testid="package-name">
              {pkg.name}
            </h1>
            <p className="mt-5 text-slate-300 text-lg">{pkg.short_description}</p>
            <div className="mt-4 text-atc-green text-sm uppercase tracking-[0.18em]" data-testid="package-price">
              {pkg.price || "Request a Quote"}
            </div>

            <div
              className="rich-content mt-10 text-slate-300 text-base leading-relaxed"
              dangerouslySetInnerHTML={{ __html: pkg.description_html || "" }}
              data-testid="package-description"
            />

            {pkg.features?.length > 0 && (
              <div className="mt-10">
                <div className="text-xs uppercase tracking-[0.22em] text-atc-blue mb-5">Highlights</div>
                <ul className="space-y-3">
                  {pkg.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3" data-testid={`package-feature-${i}`}>
                      <SporeBullet size={22} className="mt-0.5 shrink-0" />
                      <span className="text-slate-200">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="lg:col-span-5">
            <div className="sticky top-32">
              <div className="text-xs uppercase tracking-[0.22em] text-atc-green mb-5">Request a quote</div>
              <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight text-white mb-6">
                Tell us about your job — we'll send pricing & availability.
              </h2>
              <QuoteForm packageId={pkg.id} packageName={pkg.name} compact />
            </div>
          </div>
        </div>
      </section>
      <Footer content={home} />
    </main>
  );
};

export default PackageDetail;
